import { Tracer } from '@opentelemetry/api';
import AppContext from 'app/common/context/AppContext';
import {
    FlightDto,
    FlightModelType,
    MapTrafficVolumeListRequest,
    TimePeriod,
    FlightLevelBand,
    FlightId,
} from 'app/model/CORE';
import { ExtendedMonitoredTv, ConvectionSource, MonitoredMeasure } from 'app/model/SOL49';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { BusEvent, UnityMessageOutgoingEvent } from 'app/common/eventbus/Events';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import FlightUtils from 'app/common/util/FlightUtils';
import { MapConvection } from 'app/components/map/CoreMap';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import { MeasureMapTrafficVolume } from 'app/components/dashboard/network/map/Map';
import MapUtils from 'app/components/map/util/MapUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';
import EventBus from 'app/common/eventbus/EventBus';

import { ShowAirspacesRequest } from './ContentViewerTypes/ShowAirspacesRequest';
import { Airspace } from './ContentViewerTypes/Airspace';
import { ShowFlightsRequest } from './ContentViewerTypes/ShowFlightsRequest';
import { Flight } from './ContentViewerTypes/Flight';
import { UnityUtils } from './UnityUtils';
import { Convection } from './ContentViewerTypes/Convection';
import { ShowConvectionsRequest } from './ContentViewerTypes/ShowConvectionsRequest';
import { ShowMeasuresRequest } from './ContentViewerTypes/ShowMeasuresRequest';
import { Measure } from './ContentViewerTypes/Measure';

export class UnityRequests {
    // NOTE(bjarne): Storing this so we can load it in on first startup if there's already something selected.
    private storedEvents = new Map<string, BusEvent>();

    // TODO(bjarne): We're storing this just so we can pull up the details when requested. Ideally we'd get
    // this data by querying the backend or something, but I'm not sure how big of a time sink/refactor that
    // would be.
    private storedFlightDtos: FlightDto[] = [];

    private tracer: Tracer;
    private eventBus: EventBus;
    private unityUtils: UnityUtils;

    public constructor(tracer: Tracer, eventBus: EventBus, unityUtils: UnityUtils) {
        this.tracer = tracer;
        this.eventBus = eventBus;
        this.unityUtils = unityUtils;
    }

    public async sendTVsToUnityAsync(tvs: Array<ExtendedMonitoredTv>) {
        const span = this.tracer.startSpan('sendTVsToUnityAsync');

        const request: MapTrafficVolumeListRequest = {
            tvIdList: tvs.map(tv => tv.refLocation.id),
            dataset: AppContext.getDataset(),
        };
        const result = await ServiceFactory.coreService().getMapTrafficVolumeList(request);
        if (!result.error) {
            const newRequest = new ShowAirspacesRequest({ airspaces: [] });

            result.data.forEach(mapTrafficVolume => {
                newRequest.airspaces.push(
                    new Airspace({
                        id: mapTrafficVolume.trafficVolume?.id, // TODO(bjarne): mtv.location.airspace.reference.id ???
                        // volumes: [originalMapTrafficVolumeToVolume(mtv)],
                        volumes: [this.unityUtils.getVolumeFromMapTrafficVolume(mapTrafficVolume)],
                    }),
                );
            });

            this.publishRequestAndStore('ShowAirspacesRequest', newRequest);
        }

        span.end();
    }

    public async sendFlightsToUnityAsync(
        flights: FlightDto[],
        highlightedSegments: Map<FlightId, ProfileSegmentHighlight[]>,
    ) {
        const span = this.tracer.startSpan('sendFlightsToUnity');

        const resultFlights = flights.map(flight => {
            let segmentHighlights = highlightedSegments.get(flight.flightId) ?? [];
            segmentHighlights = segmentHighlights
                .slice()
                .sort((a, b) => TimeUtils.compare(a.timePeriod.from, b.timePeriod.from));

            const currentflightPlan = this.unityUtils.getFlightPlan(flight, FlightModelType.CTFM, segmentHighlights);
            const filedflightPlan = this.unityUtils.getFlightPlan(flight, FlightModelType.FTFM, segmentHighlights);
            const regulatedflightPlan = this.unityUtils.getFlightPlan(flight, FlightModelType.RTFM, segmentHighlights);

            // TODO(bjarne): Is this the right way of retrieving the aerodromes?
            const currentPoints = FlightUtils.getPoints(flight, FlightModelType.CTFM) ?? [];
            const firstAerodrome = currentPoints.find(x => x.aerodrome); // flight.flightId.flightKeys.adep
            const lastAerodrome = currentPoints.findLast(x => x.aerodrome); // flight.flightId.flightKeys.ades

            return new Flight({
                id: flight.flightId?.id,
                aircraft: flight.aircraftAddress, // flight.flightId?.flightKeys.callSign,
                aerodromeDeparture: firstAerodrome ? this.unityUtils.getAerodrome(firstAerodrome) : undefined,
                aerodromeDestination: lastAerodrome ? this.unityUtils.getAerodrome(lastAerodrome) : undefined,
                currentflightPlan: currentflightPlan,
                filedflightPlan: filedflightPlan,
                regulatedflightPlan: regulatedflightPlan,
            });
        });

        const newRequest = new ShowFlightsRequest({ flights: resultFlights });

        this.storedFlightDtos = flights;
        this.publishRequestAndStore('ShowFlightsRequest', newRequest);

        span.end();
    }

    public sendConvectionsToUnity(
        convections: MapConvection[],
        showTVs: boolean,
        flightLevelFilter?: FlightLevelBand,
        timePeriod?: TimePeriod,
        hideConvectionWithNoImpact: boolean = false,
    ) {
        const span = this.tracer.startSpan('sendConvectionsToUnity');

        const filtered =
            flightLevelFilter === undefined
                ? convections
                : convections.filter(mapConvection => {
                      return FlightLevelUtils.intersects(mapConvection.convection.flightLevelBand, flightLevelFilter);
                  });

        const convectionSource = AppContext.getConvectionSource();
        let resultConvections: Convection[];
        if (convectionSource == ConvectionSource.DWD || convectionSource == ConvectionSource.DWD_ICONV) {
            resultConvections = this.unityUtils.getPolygonConvections(
                filtered,
                showTVs,
                hideConvectionWithNoImpact,
                timePeriod,
            );
        } else {
            resultConvections = this.unityUtils.getTvConvections(filtered);
        }

        const newRequest: ShowConvectionsRequest = new ShowConvectionsRequest({ convections: resultConvections });
        this.publishRequestAndStore('ShowConvectionsRequest', newRequest);

        span.end();
    }

    public sendMeasuresToUnity(
        mtvs: MeasureMapTrafficVolume[],
        monitoredMeasures: MonitoredMeasure[],
        flightLevelFilter: FlightLevelBand,
    ) {
        const span = this.tracer.startSpan('sendMeasuresToUnity');

        const measures = mtvs
            .filter(mtv => MapUtils.intersects(mtv, flightLevelFilter))
            .map(mtv => {
                const measure = monitoredMeasures.find(x => x.measure.measureId.id === mtv.measureId);
                // const isRegulation = measure.measure.measureId.type === MeasureType.REGULATION;
                // console.log('Found measure: ', measure, isRegulation);
                const delayInMinutes = measure?.delay / TimeUtils.MIN_TO_MS;
                return new Measure({
                    id: mtv.measureId,
                    delayInMinutes: delayInMinutes,
                    // TODO(bjarne): Look into unioning the polygons if we want (MapUtils.ts:607>619).
                    volumes: [this.unityUtils.getVolumeFromMapTrafficVolume(mtv)],
                });
            });

        const newRequest = new ShowMeasuresRequest({ measures: measures });
        this.publishRequestAndStore('ShowMeasuresRequest', newRequest);

        span.end();
    }

    private publishRequestAndStore(requestType: string, requestData: object) {
        const newEvent = new UnityMessageOutgoingEvent(JSON.stringify({ type: requestType, data: requestData }));
        this.storedEvents.set(requestType, newEvent);
        this.eventBus.publish(newEvent);
    }

    public sendStoredUnityRequests() {
        this.storedEvents.forEach(event => {
            this.eventBus.publish(event);
        });
    }

    public showFlightDetailFromUnity(flightID: string) {
        const foundFlight = this.storedFlightDtos.find(x => x.flightId?.id === flightID);
        if (foundFlight) {
            ModalHandler.showFlightDetailsPanel(foundFlight.flightId, foundFlight.dataset, foundFlight.proposalContext);
        }
    }
}
